import React, { useState } from 'react';
import {
  ChevronDown, ChevronUp, Upload, Building2, Package, FileText,
  FileSpreadsheet, Users, ArrowLeftRight, ListOrdered,
} from 'lucide-react';

// On-page working guide for the B2B Trade Portal. Purely instructional — it
// spells out the order of operations so a new admin doesn't have to be told
// which action follows which. Collapsed state is remembered for the session so
// it doesn't get in the way once the flow is known.

const STORAGE_KEY = 'b2b_guide_collapsed';

const STEPS = [
  {
    icon: Upload,
    title: 'Upload the PO',
    body: 'Drop the customer\'s purchase order PDF into the drop zone. The line items, PO number and delivery address are read automatically.',
  },
  {
    icon: Building2,
    title: 'Review & assign an account',
    body: 'Check the parsed lines, then assign the PO to a trade account. A trade discount on the account is applied to every line automatically.',
  },
  {
    icon: Package,
    title: 'Allocate stock',
    body: 'Open the order and tick ALLOCATE against each line as stock is picked. When every line is allocated the status moves to Stock Allocated.',
  },
  {
    icon: FileText,
    title: 'Generate the invoice',
    body: 'Click GENERATE INVOICE. This assigns the next number in the system-wide sequence, builds the PDF and sets the status to Invoiced.',
  },
  {
    icon: FileSpreadsheet,
    title: 'Reconcile for Sage',
    body: 'Click RECONCILE THIS PO to stage it, then export from Admin → Invoices → Sage Export. Enter the Sage number against the row once it has posted.',
  },
];

const TABS = [
  { icon: FileText, name: 'Purchase Orders', body: 'Upload POs, allocate stock, generate invoices and stage orders for Sage.' },
  { icon: Building2, name: 'Trade Accounts', body: 'Trade customer records — contact details, terms, discounts, credit checks and portal invites.' },
  { icon: Users, name: 'Consumer Accounts', body: 'Retail customer records imported from Sage, with their invoices and entries.' },
  { icon: ArrowLeftRight, name: 'Data Import / Export', body: 'Bring in Sage customers and POs, and reconcile invoices against Sage.' },
];

export default function B2BHowItWorks() {
  // Collapsed by default — the guide only expands when an admin asks for it.
  // An expansion is remembered for the session so it doesn't keep re-opening.
  const [collapsed, setCollapsed] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) !== '0'; } catch (_e) { return true; }
  });

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { sessionStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch (_e) { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="border border-white/10 bg-white/[0.02] mb-8">
      <button
        onClick={toggle}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors"
      >
        <ListOrdered className="w-4 h-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-white/70 text-[10px] font-bold tracking-widest">HOW THIS PAGE WORKS</p>
          <p className="text-white/30 text-xs mt-0.5">
            {collapsed
              ? 'Upload → assign account → allocate stock → invoice → reconcile for Sage'
              : 'Work a purchase order through the five steps below, in order.'}
          </p>
        </div>
        <span className="text-white/30 text-[10px] font-bold tracking-widest shrink-0">
          {collapsed ? 'SHOW' : 'HIDE'}
        </span>
        {collapsed ? <ChevronDown className="w-4 h-4 text-white/30 shrink-0" /> : <ChevronUp className="w-4 h-4 text-white/30 shrink-0" />}
      </button>

      {!collapsed && (
        <div className="border-t border-white/10 px-5 py-5 space-y-6">
          {/* Order of operations */}
          <div>
            <p className="text-white/30 text-[10px] font-bold tracking-widest mb-3">ORDER OF OPERATIONS</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {STEPS.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <div key={step.title} className="border border-white/10 bg-[#111115] p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-5 h-5 flex items-center justify-center border border-primary/40 bg-primary/10 text-primary text-[10px] font-bold shrink-0">
                        {idx + 1}
                      </span>
                      <Icon className="w-3.5 h-3.5 text-primary/70 shrink-0" />
                    </div>
                    <p className="text-white font-bold text-xs mb-1.5">{step.title}</p>
                    <p className="text-white/40 text-[11px] leading-relaxed">{step.body}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tab guide */}
          <div>
            <p className="text-white/30 text-[10px] font-bold tracking-widest mb-3">WHAT EACH TAB IS FOR</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <div key={tab.name} className="flex items-start gap-2.5 py-1.5">
                    <Icon className="w-3.5 h-3.5 text-white/30 shrink-0 mt-0.5" />
                    <p className="text-[11px] leading-relaxed">
                      <span className="text-white/70 font-bold">{tab.name}</span>
                      <span className="text-white/40"> — {tab.body}</span>
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-white/30 text-[11px] leading-relaxed border-t border-white/10 pt-4">
            The invoice is always generated before the PO is staged for Sage — the Sage export posts against the invoice number, not the PO number.
          </p>
        </div>
      )}
    </div>
  );
}